<script>
  import { createEventDispatcher, getContext, onMount } from "svelte";
  import {
    createRandomVisibleEncoding,
    createRandomCleanEncoding,
    renderToCanvas,
  } from "bitframes";
  import Button from "./Button.svelte";
  import prng from "../util/global-prng.js";
  import {
    colorSpace,
    getBackground,
    isAfterLaunchStore,
    LINK_CREATE,
  } from "../util/constants";
  import MintButton from "./MintButton.svelte";
  import diceIcon from "../icons/dice.svelte";
  import RoundLinkButton from "./RoundLinkButton.svelte";

  export let useGlobalState = true;
  export let system = 0;
  export let allowMint = false;

  const defaultBackground = getBackground({ system });

  let lastCanvasStyleWidth;
  let lastCanvasStyleHeight;
  // let hasBeenSeen = false;

  let isSmall = false;
  $: {
    // isSmall = $windowState.width < 900;
  }
  // export let showShuffle = true;

  const flipCanvas = document.createElement("canvas");
  const flipContext = flipCanvas.getContext("2d", { colorSpace });
  flipCanvas.width = maxResolution;
  flipCanvas.height = maxResolution;

  const randomFast = (a, b) => {
    return createRandomVisibleEncoding(a, b);
  };
  let encoding = randomFast(prng, { system });
  let flipDirty = true;

  let targetRandomEncoding;
  let targetGlobalEncoding;
  const globalEncStore = getContext("encoding");

  // export let targetEncoding = randomFast();

  let canvas, context, raf;

  const FPS = 14;
  const flipDelay = 1000 / FPS;
  const resetDelay = 2000;

  const maxResolution = 1280;
  const minResolution = 256;
  const framesBeforeStop = FPS;
  let intermediates = [];

  let canvasTargetWidth = "auto",
    canvasTargetHeight = "auto";

  let flipping = true;
  let frames = 0;
  let flipElapsed = 0;
  let animElapsed = 0;
  let then = performance.now();
  let dispatch = createEventDispatcher();
  let hasBeenSeen = false;

  sizeLogic();

  onMount(() => {
    const unsub = globalEncStore.subscribe((e) => {
      targetGlobalEncoding = e;
      if (!flipping) {
        flipDirty = true;
      }
      if (useGlobalState) {
        renderFlip();
      }
      if (!flipping) {
        render();
      }
      // console.log("got new target", e.length);
    });
    if (!useGlobalState) {
      targetRandomEncoding = createRandomCleanEncoding(prng, { system });
    }
    context = canvas.getContext("2d", { colorSpace });
    canvas.width = maxResolution;
    canvas.height = maxResolution;
    context.fillStyle = defaultBackground;
    context.fillRect(0, 0, maxResolution, maxResolution);

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]) {
        const visible = entries[0].isIntersecting;
        const wasVisible = entries[0].target.dataset.visible === "true";
        entries[0].target.dataset.visible = visible ? "true" : "false";
        if (visible !== wasVisible) {
          // if (!hasBeenSeen) {
          //   hasBeenSeen = true;
          // }
          if (visible) start();
          else stop();
        }
      }
    });

    // updateTweens();
    observer.observe(canvas);
    setFlip(true);
    if (!useGlobalState) renderFlip();
    next();
    sizeLogic();

    return () => {
      observer.unobserve(canvas);
      stop();
      unsub();
    };
  });

  // function updateTweens() {
  //   intermediates.length = 0;
  //   for (let i = 0; i < framesBeforeStop; i++) {
  //     intermediates.push(randomFast());
  //   }
  // }

  function renderFlip() {
    if (!flipContext) return;
    if (!flipDirty) return;
    const encoding = useGlobalState
      ? targetGlobalEncoding
      : targetRandomEncoding;
    renderToCanvas({
      encoding,
      colorSpace,
      width: flipCanvas.width,
      height: flipCanvas.height,
      context: flipContext,
      hatch: true,
      hatchContours: true,
    });
    flipDirty = false;
  }

  function start() {
    then = performance.now();
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(tick);
  }

  function stop() {
    cancelAnimationFrame(raf);
  }

  function next(enc) {
    encoding = enc || randomFast(prng, { system });
    render();
    frames++;
  }

  function tick(now) {
    const dt = now - then;

    if (!hasBeenSeen) {
      hasBeenSeen = true;
    }

    if (flipping) {
      flipElapsed += dt;
      if (flipElapsed >= flipDelay) {
        flipElapsed %= flipDelay;

        if (frames >= framesBeforeStop) {
          setFlip(false);
          const targ = useGlobalState
            ? targetGlobalEncoding
            : targetRandomEncoding;
          next(targ);
        } else {
          next();
        }
      }
    } else {
      animElapsed += dt;
      if (animElapsed >= resetDelay) {
        animElapsed %= resetDelay;
      }
    }
    then = now;
    raf = requestAnimationFrame(tick);
  }

  function setFlip(f) {
    flipping = f;
    if (!canvas) return;
    // canvas.width = flipping ? minResolution : maxResolution;
    // canvas.height = flipping ? minResolution : maxResolution;
  }

  function shuffle() {
    dispatch("randomize");
    flipElapsed = 0;
    if (!useGlobalState) {
      targetRandomEncoding = createRandomCleanEncoding(prng, { system });
    }
    frames = 0;
    setFlip(true);
    flipDirty = true;
    if (!useGlobalState) renderFlip();
    next();
    // console.log("shuffling");
  }

  function render() {
    if (!context) return;
    if (flipping) {
      context.globalAlpha = 1;
      context.clearRect(0, 0, canvas.width, canvas.height);
      renderToCanvas({
        encoding,
        colorSpace,
        width: canvas.width,
        height: canvas.height,
        context,
        hatch: true,
        hatchContours: false,
      });
      // canvas.style.imageRendering = "pixelated";
    } else {
      canvas.style.imageRendering = "";
      context.globalAlpha = 1;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(flipCanvas, 0, 0, canvas.width, canvas.height);
    }
  }

  function sizeLogic() {
    let minDim = Math.min(window.innerWidth, window.innerHeight) * 0.75;
    if (window.innerWidth <= 720) {
      // mobile: max to width but ensure height is not too large
      const size = minDim * 1;
      canvasTargetWidth = `calc(min(100%, 70vh))`;
      // canvasTargetWidth = `${size}px`;
    } else if (window.innerWidth <= 900) {
      // canvasTargetWidth = `50vw`;
      // canvasTargetWidth = `${window.innerWidth * 0.5}px`;
    }
    canvasTargetWidth = `calc(min(50vw, 70vh))`;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      if (
        lastCanvasStyleWidth !== rect.width ||
        lastCanvasStyleHeight !== rect.height
      ) {
        lastCanvasStyleWidth = rect.width;
        lastCanvasStyleHeight = rect.height;
        dispatch("resized", {
          width: lastCanvasStyleWidth,
          lastCanvasStyleHeight,
        });
      }
    }
    // canvasTargetWidth = `${w}px`;
    // canvasTargetHeight = `${w}px`;
  }
</script>

<svelte:window on:resize|passive={sizeLogic} />
<div class="container">
  <!-- <div class="card">
    <StaticPunchedCard
      encoding={useGlobalState ? targetGlobalEncoding : targetRandomEncoding}
    />
  </div> -->
  {#if true}
    <div class="toolbar">
      <div class="toolbar-inner">
        <!-- <Button small text="random" on:click={shuffle}></Button> -->
        <Button
          on:click={shuffle}
          small={isSmall}
          hotkey="r"
          icon={diceIcon}
          text="Random"
        />
      </div>
      <div class="toolbar-inner">
        {#if $isAfterLaunchStore}
          <RoundLinkButton
            href={`/${LINK_CREATE}`}
            small={isSmall}
            hotkey="e"
            text="Edit Design"
          />
        {:else if allowMint}
          <MintButton small={isSmall} />
        {/if}
      </div>
    </div>
  {/if}
  <canvas bind:this={canvas} data-visible="false" />
</div>

<style>
  .container {
    /* --min-height: 400px; */
    --min-height: calc(min(calc(100vw - var(--main-gutter)), 400px));
    padding: 0px;
    display: flex;
    justify-content: flex-start;
    flex-direction: column;
  }
  /* .card {
    margin-bottom: 10px;
    max-width: calc(min(calc(100vw - 40px), 600px));
  } */
  .button-link {
    text-decoration: none;
    font-weight: inherit;
  }
  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 20px;
    /* margin-top: 10px; */
    gap: 10px;
  }
  .toolbar-inner {
    display: flex;
    gap: 10px;
    justify-content: space-between;
    align-items: center;
  }
  .container {
    aspect-ratio: 1;
    /* width: calc(max(50vw)); */
    /* width: calc(max(--min-height, 50vw)); */
    /* width: calc(max(var(--min-height), min(50vw, 80vh))); */
    height: auto;
  }
  canvas {
    width: 100%;
    height: auto;
  }
  @media screen and (max-width: 720px) {
    .container {
      /* width: calc(max(var(--min-height), min(100%, 70vh))); */
    }
  }
  @media screen and (max-height: 450px) {
    .container {
      /* width: calc(min(50%)); */
    }
  }
</style>
